import { useEffect, useState } from "react";

const T = {
  navy: "#0A1E45",
  navy2: "#071530",
  gold: "#C9A540",
  gold2: "#E4C87A",
  white: "#FFFFFF",
  offwhite: "#FAFAF7",
  cream: "#F5F1E8",
  muted: "#64647A",
  border: "#DDD8CE",
  text: "#374151",
  ffHead: "'Playfair Display', Georgia, serif",
  ffBody: "'DM Sans', system-ui, sans-serif",
};

const DPO_EMAIL = "data.protection@eusbd.eu";

const globalCSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { font-family: ${T.ffBody}; font-size: 16px; color: ${T.navy}; background: ${T.white}; -webkit-font-smoothing: antialiased; }
  .pp-page { overflow-x: hidden; }
  .pp-layout > * { min-width: 0; }
  .pp-content { min-width: 0; }
  .pp-table-wrap { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .pp-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
  
  @media (max-width: 900px) {
    .pp-layout { grid-template-columns: 1fr !important; gap: 32px !important; padding-left: 24px !important; padding-right: 24px !important; }
    .pp-toc { position: relative !important; top: auto !important; }
  }

  @media (max-width: 600px) {
    .pp-hero { padding: 64px 16px 52px !important; }
    .pp-hero-ring { width: 260px !important; height: 260px !important; top: -90px !important; right: -120px !important; }
    .pp-hero-inner p { font-size: 15px !important; line-height: 1.65 !important; }
    .pp-hero-badge { font-size: 11px !important; padding: 6px 12px !important; letter-spacing: 0.08em !important; }
    .pp-layout { padding-left: 20px !important; padding-right: 20px !important; }
    .pp-content h2 { font-size: 22px !important; }
    .pp-rights-grid { grid-template-columns: 1fr !important; }
  }
`;

function useGlobalStyles() {
  useEffect(() => {
    const id = "esbd-pp-global";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = globalCSS;
      document.head.appendChild(style);
    }
  }, []);
}

function PPTable({ columns, rows }) {
  const thStyle = {
    textAlign: "left",
    padding: "12px 16px",
    background: T.navy,
    color: T.white,
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };
  const tdStyle = {
    padding: "14px 16px",
    borderBottom: "1px solid #F0ECE4",
    color: T.text,
    lineHeight: 1.6,
    verticalAlign: "top",
    fontSize: 14,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };
  return (
    <div className="pp-table-wrap">
      <table className="pp-table">
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={i} style={thStyle}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={i % 2 === 1 ? { background: T.offwhite } : {}}>
              {row.map((cell, j) => (
                <td key={j} style={tdStyle} dangerouslySetInnerHTML={{ __html: cell }} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RightCard({ title, children }) {
  return (
    <div style={{ background: T.offwhite, padding: "20px 24px", borderLeft: `3px solid ${T.gold}` }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.navy, marginBottom: 6 }}>{title}</div>
      <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.65, margin: 0 }}>{children}</p>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} style={{ marginBottom: 48, paddingBottom: 48, borderBottom: "1px solid #F0ECE4" }}>
      <h2 style={{ fontFamily: T.ffHead, fontSize: 26, fontWeight: 700, color: T.navy, marginBottom: 20, lineHeight: 1.25 }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function P({ children, ...props }) {
  return <p style={{ fontSize: 15, color: T.text, lineHeight: 1.8, marginBottom: 14, ...props.style }}>{children}</p>;
}

const NAV = [
  { id: "controller", label: "1. Data Controller" },
  { id: "collect", label: "2. What We Collect" },
  { id: "basis", label: "3. Legal Basis" },
  { id: "use", label: "4. How We Use Your Data" },
  { id: "recipients", label: "5. Data Recipients" },
  { id: "transfers", label: "6. International Transfers" },
  { id: "retention", label: "7. Retention" },
  { id: "rights", label: "8. Your Rights" },
  { id: "cookies", label: "9. Cookies" },
  { id: "children", label: "10. Children" },
  { id: "changes", label: "11. Policy Changes" },
  { id: "contact", label: "12. Contact & Complaints" },
];

export default function PrivacyPolicyPage() {
  useGlobalStyles();
  const [activeId, setActiveId] = useState(NAV[0].id);

  useEffect(() => {
    const onScroll = () => {
      const offsetTop = window.scrollY + 150;
      for (const item of NAV) {
        const section = document.getElementById(item.id);
        if (!section) continue;
        if (
          section.offsetTop <= offsetTop &&
          section.offsetTop + section.offsetHeight > offsetTop
        ) {
          setActiveId(item.id);
          break;
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveId(id);
    }
  };

  return (
    <div className="pp-page">
      <div className="pp-hero" style={{ background: T.navy, padding: "80px 40px 70px", position: "relative", overflow: "hidden" }}>
        <div
          className="pp-hero-ring"
          style={{
            position: "absolute",
            top: -80,
            right: -80,
            width: 400,
            height: 400,
            border: "1px solid rgba(201,165,64,0.1)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />
        <div className="pp-hero-inner" style={{ maxWidth: 820, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: T.gold,
              marginBottom: 20,
            }}
          >
            <span style={{ width: 28, height: 1, background: T.gold, display: "block" }} />
            Legal
          </div>
          <h1
            style={{
              fontFamily: T.ffHead,
              fontSize: "clamp(32px, 5vw, 48px)",
              fontWeight: 700,
              color: T.white,
              lineHeight: 1.15,
              marginBottom: 16,
            }}
          >
            Privacy Policy
          </h1>
          <p style={{ fontSize: 17, color: "rgba(255,255,255,0.6)", lineHeight: 1.75, maxWidth: 600 }}>
            How the European School of Business and Diplomacy collects, uses, and protects your personal data under the General Data
            Protection Regulation.
          </p>
          <span
            className="pp-hero-badge"
            style={{
              display: "inline-block",
              marginTop: 24,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: T.navy,
              background: T.gold,
              padding: "6px 16px",
            }}
          >
            Effective: 1 April 2026
          </span>
        </div>
      </div>

      <div
        className="pp-layout"
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: "60px 40px 80px",
          display: "grid",
          gridTemplateColumns: "240px 1fr",
          gap: 56,
        }}
      >
        <nav className="pp-toc" style={{ position: "sticky", top: 32, alignSelf: "start" }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: T.muted,
              marginBottom: 16,
              paddingBottom: 10,
              borderBottom: `1px solid ${T.border}`,
            }}
          >
            Contents
          </div>
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              onClick={(e) => handleNavClick(e, n.id)}
              style={{
                display: "block",
                fontSize: 13.5,
                color: activeId === n.id ? T.navy : T.muted,
                textDecoration: "none",
                padding: "8px 0 8px 14px",
                borderLeft: activeId === n.id ? `2px solid ${T.gold}` : "2px solid transparent",
                lineHeight: 1.5,
                transition: "all 0.15s",
                fontWeight: activeId === n.id ? 700 : 500,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = T.navy;
                e.currentTarget.style.borderLeftColor = T.gold;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = activeId === n.id ? T.navy : T.muted;
                e.currentTarget.style.borderLeftColor =
                  activeId === n.id ? T.gold : "transparent";
              }}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="pp-content">
          <Section id="controller" title="1. Data Controller">
            <P>The data controller for the personal information described in this policy is:</P>
            <P>
              <strong>European School of Business and Diplomacy (ESBD)</strong>
              <br />
              241 Tower Business Centre, 2nd Floor, Triq it-Torri
              <br />
              Swatar, Birkirkara BKR 4013
              <br />
              Malta
            </P>
            <P>
              For data protection enquiries, contact:{" "}
              <strong>
                <a href={`mailto:${DPO_EMAIL}`} style={{ color: T.navy }}>
                  {DPO_EMAIL}
                </a>
              </strong>
            </P>
          </Section>

          <Section id="collect" title="2. What We Collect">
            <P>
              The personal data we collect depends on how you interact with ESBD. The table below summarises the categories of data,
              the source, and the context in which it is collected.
            </P>
            <PPTable
              columns={["Context", "Data Collected"]}
              rows={[
                [
                  "<strong>Admissions Application</strong><br/><em style='font-size:12px;color:#9CA3AF'>admissions.eusbd.eu</em>",
                  "Full name, date of birth, nationality, gender, email, phone, postal address, educational qualifications, employment history, personal statement, passport-style photograph, uploaded supporting documents (transcripts, certificates, ID).",
                ],
                [
                  "<strong>Study Centre Partner (SCP) Expression of Interest</strong>",
                  "Full name, organisation name, role/title, email, phone/WhatsApp, country, city, organisation type, website, description of the organisation and partnership interest.",
                ],
                [
                  "<strong>Corporate Partnership Expression of Interest</strong>",
                  "Full name, organisation name, role/title, email, phone/WhatsApp, country, city, organisation type, website, description of partnership interest.",
                ],
                [
                  "<strong>Portal Account</strong><br/><em style='font-size:12px;color:#9CA3AF'>Applicants and Staff</em>",
                  "Username, hashed password, profile photograph, login timestamps, IP addresses.",
                ],
                [
                  "<strong>Payments</strong>",
                  "Payment amounts, dates, method (Stripe, bank transfer), transaction references. We do not store full card numbers. Card payments are processed by Stripe, who acts as an independent data controller.",
                ],
                [
                  "<strong>Website Analytics</strong>",
                  "Anonymised browsing data (pages visited, referral source, device type, approximate location) via cookies. See Section 9.",
                ],
              ]}
            />
          </Section>

          <Section id="basis" title="3. Legal Basis for Processing">
            <P>We process your personal data under the following legal bases as defined by the GDPR (Article 6):</P>
            <PPTable
              columns={["Purpose", "Legal Basis"]}
              rows={[
                [
                  "Assessing your admissions application",
                  "<strong>Contract</strong> — processing is necessary for steps at your request prior to entering a contract (Article 6(1)(b)).",
                ],
                [
                  "Assessing SCP or Corporate Partnership applications",
                  "<strong>Consent</strong> — you provide explicit consent via the checkbox on the expression of interest form (Article 6(1)(a)).",
                ],
                [
                  "Managing your student account, enrolment, and fees",
                  "<strong>Contract</strong> — necessary for performing our contractual obligations to you as a student (Article 6(1)(b)).",
                ],
                [
                  "Sending transactional emails (confirmations, status updates)",
                  "<strong>Contract / Consent</strong> — depending on context, necessary for the service you have engaged with.",
                ],
                [
                  "Complying with legal and regulatory obligations (e.g. MFHEA reporting)",
                  "<strong>Legal Obligation</strong> — processing is necessary for compliance (Article 6(1)(c)).",
                ],
                [
                  "Internal analytics and service improvement",
                  "<strong>Legitimate Interest</strong> — we have a legitimate interest in understanding how our services are used, balanced against your rights (Article 6(1)(f)).",
                ],
              ]}
            />
            <P>
              Where consent is the legal basis, you may withdraw it at any time by contacting us at{" "}
              <strong>
                <a href={`mailto:${DPO_EMAIL}`} style={{ color: T.navy }}>
                  {DPO_EMAIL}
                </a>
              </strong>
              . Withdrawal does not affect the lawfulness of processing carried out before the withdrawal.
            </P>
          </Section>

          <Section id="use" title="4. How We Use Your Data">
            <P>We use your personal data for the following purposes:</P>
            <P>
              To assess and process your application for admission, partnership, or other engagement with ESBD. To communicate with you
              about your application status, offer letters, payment schedules, and enrolment. To provide access to your applicant or
              partner portal account. To generate offer letters and academic records. To comply with quality assurance requirements set
              by the Malta Further and Higher Education Authority (MFHEA) and other relevant regulatory bodies. To produce anonymised
              statistical reports for institutional planning. To respond to your enquiries and provide support.
            </P>
            <P>We do not use your personal data for automated decision-making or profiling that produces legal effects.</P>
          </Section>

          <Section id="recipients" title="5. Data Recipients">
            <P>Your personal data may be shared with the following categories of recipients, strictly on a need-to-know basis and under appropriate safeguards:</P>
            <PPTable
              columns={["Recipient", "Purpose", "Safeguard"]}
              rows={[
                ["<strong>Brevo (Sendinblue)</strong>", "Sending transactional emails (confirmations, status updates, offer notifications)", "EU-based data processing agreement; GDPR-compliant"],
                ["<strong>Stripe</strong>", "Processing card payments", "Independent data controller; PCI-DSS Level 1 certified"],
                ["<strong>IONOS (hosting)</strong>", "Hosting the admissions portal and website", "EU-based data centres; data processing agreement"],
                ["<strong>MFHEA and regulatory bodies</strong>", "Compliance reporting and quality assurance audits", "Legal obligation under Maltese education law"],
                ["<strong>ESBD Study Centre Partners</strong>", "Limited data (name, enrolment status) shared only for students referred by that specific partner", "Data sharing agreement; minimum necessary principle"],
              ]}
            />
            <P>We do not sell your personal data to any third party. We do not share your data with advertisers.</P>
          </Section>

          <Section id="transfers" title="6. International Transfers">
            <P>ESBD is based in Malta, a member state of the European Union. Your data is primarily stored and processed within the EU/EEA.</P>
            <P>
              Where data is transferred outside the EU/EEA (for example, if a third-party service provider such as Stripe processes data in the United States), we ensure that appropriate safeguards are in place, such as Standard Contractual Clauses (SCCs) approved by the European Commission, an adequacy decision, or the recipient&apos;s participation in an approved certification mechanism.
            </P>
          </Section>

          <Section id="retention" title="7. Data Retention">
            <P>We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected. Specific retention periods are as follows:</P>
            <PPTable
              columns={["Data Category", "Retention Period"]}
              rows={[
                ["Successful admissions applications and student records", "Duration of studies plus 6 years (for academic record-keeping and regulatory compliance)."],
                ["Unsuccessful admissions applications", "12 months from the date of the final decision, then deleted."],
                ["SCP or Corporate Partnership applications (approved)", "Duration of the partnership plus 2 years."],
                ["SCP or Corporate Partnership applications (declined)", "12 months from the date of the decision, then deleted."],
                ["Payment records", "7 years (Maltese tax and accounting obligations)."],
                ["Portal login and audit logs", "90 days."],
                ["Website analytics cookies", "See Section 9."],
              ]}
            />
            <P>When data reaches the end of its retention period, it is securely deleted or anonymised.</P>
          </Section>

          <Section id="rights" title="8. Your Rights Under GDPR">
            <P>As a data subject, you have the following rights under the General Data Protection Regulation. These rights apply regardless of your location, because ESBD processes your data under EU law.</P>
            <div className="pp-rights-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, margin: "24px 0" }}>
              <RightCard title="Right of Access (Article 15)">You have the right to request a copy of the personal data we hold about you, and to know how it is being used.</RightCard>
              <RightCard title="Right to Rectification (Article 16)">You can request that we correct any inaccurate or incomplete personal data we hold about you.</RightCard>
              <RightCard title="Right to Erasure (Article 17)">You can request deletion of your personal data where it is no longer necessary, or where you withdraw consent.</RightCard>
              <RightCard title="Right to Restrict Processing (Article 18)">You can request that we limit how we use your data in certain circumstances, such as while a complaint is being resolved.</RightCard>
              <RightCard title="Right to Data Portability (Article 20)">You can request a machine-readable copy of data you provided to us, where processing is based on consent or contract.</RightCard>
              <RightCard title="Right to Object (Article 21)">You can object to processing based on legitimate interest. We will stop unless we can demonstrate compelling grounds.</RightCard>
              <RightCard title="Right to Withdraw Consent">Where processing is based on your consent, you may withdraw it at any time without affecting the lawfulness of prior processing.</RightCard>
              <RightCard title="Right to Lodge a Complaint">You have the right to lodge a complaint with the Office of the Information and Data Protection Commissioner (IDPC) in Malta.</RightCard>
            </div>
            <P>
              To exercise any of these rights, please email{" "}
              <strong>
                <a href={`mailto:${DPO_EMAIL}`} style={{ color: T.navy }}>
                  {DPO_EMAIL}
                </a>
              </strong>{" "}
              with the subject line &quot;Data Protection Request&quot;. We will respond within 30 days.
            </P>
          </Section>

          <Section id="cookies" title="9. Cookies">
            <P><strong>Strictly necessary cookies:</strong> These are required for the portal to function (e.g. session cookies for login). They cannot be disabled.</P>
            <P><strong>Analytics cookies:</strong> We may use anonymised analytics to understand how visitors interact with our website. These are only set with your consent. You can manage your cookie preferences using your browser settings or our cookie banner.</P>
            <P>We do not use advertising or tracking cookies. We do not participate in any ad networks.</P>
          </Section>

          <Section id="children" title="10. Children">
            <P>ESBD&apos;s programmes and partnership opportunities are designed for adults. We do not knowingly collect personal data from individuals under 18 years of age. If you believe we have inadvertently collected data from a minor, please contact us immediately and we will delete it.</P>
          </Section>

          <Section id="changes" title="11. Changes to This Policy">
            <P>We may update this privacy policy from time to time to reflect changes in our practices, services, or legal requirements. When we make material changes, we will update the &quot;Effective&quot; date at the top of this page. We encourage you to review this policy periodically.</P>
          </Section>

          <section id="contact" style={{ marginBottom: 0 }}>
            <h2 style={{ fontFamily: T.ffHead, fontSize: 26, fontWeight: 700, color: T.navy, marginBottom: 20, lineHeight: 1.25 }}>
              12. Contact and Complaints
            </h2>
            <P>If you have questions about this privacy policy or wish to exercise your data protection rights, please contact us:</P>

            <div style={{ background: T.navy, padding: "36px 40px", marginTop: 32 }}>
              <h3 style={{ fontFamily: T.ffHead, fontSize: 22, fontWeight: 700, color: T.white, margin: "0 0 12px" }}>
                Data Protection Contact
              </h3>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.65)", lineHeight: 1.75, marginBottom: 0 }}>
                European School of Business and Diplomacy
                <br />
                241 Tower Business Centre, 2nd Floor, Triq it-Torri
                <br />
                Swatar, Birkirkara BKR 4013, Malta
              </p>
              <p style={{ marginTop: 12, marginBottom: 0 }}>
                <a href={`mailto:${DPO_EMAIL}`} style={{ color: T.gold, textDecoration: "none", fontWeight: 600, fontSize: 14 }}>
                  {DPO_EMAIL}
                </a>
              </p>
            </div>

            <P style={{ marginTop: 24 }}>If you are not satisfied with our response, you have the right to lodge a complaint with:</P>
            <P>
              <strong>Office of the Information and Data Protection Commissioner (IDPC)</strong>
              <br />
              Level 2, Airways House, High Street
              <br />
              Sliema SLM 1549, Malta
            </P>
          </section>
        </div>
      </div>
    </div>
  );
}

